import { PdfIcon } from '@/assets/pdf-icon';
import { WordIcon } from '@/assets/word-icon';
import { useAlertDialog } from '@/components/common/alert-dialog-provider';
import { Button } from '@/components/ui/button';
import { useIsMobile } from '@/hooks/use-mobile';
import { useReportRegenerate } from '@/hooks/use-report-regenerate';
import { useReportSave } from '@/hooks/use-report-save';
import { cn } from '@/lib/utils';
import { downloadFile } from '@/lib/utils/file-download';
import {
  createPdfPreviewUrl,
  revokePdfPreviewUrl,
} from '@/lib/utils/pdf-preview';
import { copyReportToClipboard } from '@/lib/utils/report-copy';
import { Capacitor } from '@capacitor/core';
import {
  convexQuery,
  useConvexAction,
  useConvexMutation,
} from '@convex-dev/react-query';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Book,
  ChevronLeft,
  Download,
  EllipsisVertical,
  Eye,
  FileText,
  Loader2,
  RefreshCw,
  ScrollText,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { api } from '~/convex/_generated/api';
import type { Doc, Id } from '~/convex/_generated/dataModel';
import type { ReportSection } from '~/convex/lib/types';
import { NativePdfViewer } from '~/custom-plugins/native-pdf-viewer/dist/esm';
import { EditableReportTitle } from '../common/editable-report-title';
import { PdfViewer } from '../common/pdf-viewer';
import { ReportStatusBadge } from '../common/report-status-badge';
import { ResponsiveContainer } from '../layout/responsive-container';
import { Badge } from '../ui/badge';
import { Drawer, DrawerContent } from '../ui/drawer';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { ReportForm, type ReportFormValues } from './report-form';
import { TranscriptionDisplay } from './transcription-display';

export const ReportEdit = ({ report }: { report: Doc<'reports'> }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id: reportId } = useParams();
  const isMobile = useIsMobile();
  const [isTitleEditing, setIsTitleEditing] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [drawerMode, setDrawerMode] = useState<
    'preview' | 'transcription' | null
  >(null);
  const isDrawerOpen = drawerMode !== null;
  const [copySuccess, setCopySuccess] = useState(false);

  // Async preview generation (decoupled from save)
  const { mutate: generatePreview, isPending: isGeneratingPreview } =
    useMutation({
      mutationFn: useConvexAction(api.reportActions.exportPreview),
      onSuccess: async (pdfBuffer: ArrayBuffer) => {
        const url = await createPdfPreviewUrl(pdfBuffer);
        setPreviewUrl((prev) => {
          if (prev) revokePdfPreviewUrl(prev);
          return url;
        });
      },
    });

  const triggerPreview = useCallback(() => {
    generatePreview({ reportId: reportId as Id<'reports'> });
  }, [generatePreview, reportId]);

  const savedInSessionRef = useRef(false);
  const { save: saveBase, isSaving } = useReportSave({
    reportId: reportId as Id<'reports'>,
    onSaved: triggerPreview,
  });

  const save = useCallback(
    (args: Parameters<typeof saveBase>[0]) => {
      savedInSessionRef.current = true;
      saveBase(args);
    },
    [saveBase],
  );

  const { regenerate, isRegenerating } = useReportRegenerate({
    reportId: reportId as Id<'reports'>,
  });

  const alertDialog = useAlertDialog();
  const { mutate: reopenReport, isPending: isReopening } = useMutation({
    mutationFn: useConvexMutation(api.reports.markReportLifecycleState),
  });

  const handleReopen = async () => {
    const confirmed = await alertDialog.confirm({
      title: t('report:reopenConfirmTitle'),
      description: t('report:reopenConfirmDescription'),
      confirmText: t('report:reopenConfirm'),
      cancelText: t('report:cancel'),
    });

    if (confirmed) {
      reopenReport(
        {
          id: reportId as Id<'reports'>,
          status: 'draft',
          documentStale: true,
        },
        {
          onSuccess: () =>
            navigate(`/reports/${reportId}/record`, { replace: true }),
        },
      );
    }
  };

  useEffect(() => {
    return () => {
      if (previewUrl) revokePdfPreviewUrl(previewUrl);
    };
  }, [previewUrl]);

  const defaultValues: ReportFormValues = {
    generatedSections: report.generatedSections,
  };

  const { data } = useQuery(
    convexQuery(api.reports.get, { id: reportId as Id<'reports'> }),
  );

  const sections: ReportSection[] =
    data?.generatedSections ?? report.generatedSections ?? [];

  useEffect(() => {
    if (copySuccess) {
      const timer = setTimeout(() => {
        setCopySuccess(false);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [copySuccess]);

  const handleCopyReport = async () => {
    try {
      const result = await copyReportToClipboard(sections);
      if (result.ok) {
        setCopySuccess(true);
        toast.success(t('report:copyReportSuccess'));
      } else {
        const message =
          result.reason === 'no_sections' || result.reason === 'empty_content'
            ? t('report:copyReportError')
            : `${t('report:copyReportError')}: ${result.reason}`;
        toast.error(message);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      toast.error(`${t('report:copyReportError')}: ${message}`);
    }
  };

  // Initial load only: when we open a report that's stale AND we have no PDF to show.
  // When we have pdfUrl, we show it—don't generate (avoids reload). Post-save drives preview via onSaved.
  const hasTriggeredInitialPreview = useRef(false);
  useEffect(() => {
    if (data?.documentStale !== true || hasTriggeredInitialPreview.current)
      return;
    if (savedInSessionRef.current) return; // Save will drive preview; don't double-trigger
    if (data?.pdfUrl) return; // Have stored PDF; show it until save—avoids reload when stale but has PDF

    hasTriggeredInitialPreview.current = true;
    triggerPreview();
  }, [data?.documentStale, data?.pdfUrl, triggerPreview]);

  const needsGeneration =
    data?.documentStale === true || !data?.pdfUrl || !data?.docxUrl;

  const { mutate: downloadPdf, isPending: isDownloadingPdf } = useMutation({
    mutationFn: downloadFile,
  });
  const { mutate: downloadDocx, isPending: isDownloadingDocx } = useMutation({
    mutationFn: downloadFile,
  });

  const handleDownloadPdf = () => {
    if (data?.pdfUrl) {
      downloadPdf({ url: data.pdfUrl, filename: `${data.patientName}.pdf` });
    }
  };
  const handleDownloadDocx = () => {
    if (data?.docxUrl) {
      downloadDocx({
        url: data.docxUrl,
        filename: `${data.patientName}.docx`,
      });
    }
  };

  return (
    <div className="flex h-full w-full flex-col bg-white">
      <ResponsiveContainer
        scrollAwareBorder
        className="flex w-full flex-row items-center gap-3 bg-white py-4 md:pt-6">
        <Button
          className="cursor-pointer md:hidden"
          variant="ghost"
          size="icon"
          onClick={() => navigate(-1)}>
          <ChevronLeft className="size-5" />
        </Button>
        <div className="flex flex-1 items-center gap-3 overflow-hidden">
          <EditableReportTitle
            reportId={report._id}
            title={report.patientName}
            onEditingChange={setIsTitleEditing}
          />
          {!(isMobile && isTitleEditing) &&
            (isSaving ? (
              <Badge
                variant="info"
                className="flex flex-row items-center gap-1.5 pr-2">
                {t('report:saving')}
                <Loader2 className="size-3 animate-spin" />
              </Badge>
            ) : isGeneratingPreview ? (
              <Badge
                variant="info"
                className="flex flex-row items-center gap-1.5 pr-2">
                {t('report:generatingPreview')}
                <Loader2 className="size-3 animate-spin" />
              </Badge>
            ) : (
              <ReportStatusBadge status={report.status} />
            ))}
        </div>
      </ResponsiveContainer>

      <ResponsiveContainer className="scrollbar-none flex min-h-0 w-full grow gap-4 overflow-y-auto pb-4 md:overflow-y-hidden md:pb-0">
        <div className="scrollbar-none flex-1 md:overflow-y-auto md:pb-4">
          <ReportForm
            defaultValues={defaultValues}
            onCancel={() => navigate(-1)}
            onSave={save}
            copyButton={
              !Capacitor.isNativePlatform()
                ? {
                    onCopy: handleCopyReport,
                    success: copySuccess,
                    disabled: sections.length === 0,
                  }
                : undefined
            }
          />
        </div>
        <div className="hidden flex-1 flex-col md:flex">
          <Tabs defaultValue="pdf" className="flex h-full flex-col">
            <TabsList className="mr-auto mb-1">
              <TabsTrigger value="pdf">{t('report:pdfPreview')}</TabsTrigger>
              <TabsTrigger value="transcription">
                {t('home:transcription')}
              </TabsTrigger>
            </TabsList>
            <TabsContent value="pdf" className="mb-4 flex-1 overflow-hidden">
              <PdfViewer url={previewUrl ?? data?.pdfUrl ?? ''} />
            </TabsContent>
            <TabsContent
              value="transcription"
              className="flex-1 overflow-hidden">
              <TranscriptionDisplay
                reportId={report._id}
                finalText={report.rawTranscription || ''}
                nonFinalText=""
                isTranscribing={false}
                isLoading={false}
                creationTime={report._creationTime}
                hideAudioControls={false}
              />
            </TabsContent>
          </Tabs>
        </div>
      </ResponsiveContainer>

      <ResponsiveContainer className="flex w-full flex-col items-center gap-3 bg-white md:pb-4">
        <div className="flex w-full flex-row flex-nowrap items-center justify-end gap-3 border-t pt-3 md:pt-4">
          <Button
            variant="outline"
            className={cn(
              'min-w-0 flex-1',
              isMobile && 'h-12 w-12 flex-none shrink-0 border-black/30',
            )}
            size={isMobile ? 'icon' : 'default'}
            loading={isReopening}
            disabled={isSaving || isRegenerating || isReopening}
            onClick={handleReopen}
            aria-label={isMobile ? t('report:reopenForRecording') : undefined}>
            <ChevronLeft className="size-6" />
            {!isMobile && t('report:reopenForRecording')}
          </Button>
          {needsGeneration || isRegenerating ? (
            <Button
              className="min-w-0 flex-1"
              loading={isRegenerating}
              disabled={isSaving || isRegenerating}
              onClick={() => {
                regenerate();
                navigate(`/reports/${reportId}/processing`, {
                  replace: true,
                  state: { generating: true },
                });
              }}>
              <RefreshCw className="size-4" />
              {t('report:regenerate')}
            </Button>
          ) : isMobile ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  className="min-w-0 flex-1 md:flex-none"
                  loading={isDownloadingPdf || isDownloadingDocx}
                  disabled={isDownloadingPdf || isDownloadingDocx}>
                  <Download className="size-4 cursor-pointer" />
                  {t('report:download')}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent side="top">
                <DropdownMenuItem
                  className="px-3 py-2.5"
                  disabled={
                    isDownloadingPdf ||
                    isSaving ||
                    isGeneratingPreview ||
                    !data?.pdfUrl
                  }
                  onClick={(e) => {
                    e.preventDefault();
                    handleDownloadPdf();
                  }}>
                  <PdfIcon className="size-4 shrink-0" />
                  PDF
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="px-3 py-2.5"
                  disabled={
                    isDownloadingDocx ||
                    isSaving ||
                    isGeneratingPreview ||
                    !data?.docxUrl
                  }
                  onClick={(e) => {
                    e.preventDefault();
                    handleDownloadDocx();
                  }}>
                  <WordIcon className="size-4 shrink-0" />
                  Word
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Button
                className="flex-1"
                loading={isDownloadingPdf}
                disabled={
                  isDownloadingPdf ||
                  isSaving ||
                  isGeneratingPreview ||
                  !data?.pdfUrl
                }
                onClick={(e) => {
                  e.preventDefault();
                  handleDownloadPdf();
                }}>
                <Book className="size-4" />
                {t('report:downloadPdf')}
              </Button>
              <Button
                className="flex-1"
                loading={isDownloadingDocx}
                disabled={
                  isDownloadingDocx ||
                  isSaving ||
                  isGeneratingPreview ||
                  !data?.docxUrl
                }
                onClick={(e) => {
                  e.preventDefault();
                  handleDownloadDocx();
                }}>
                <FileText className="size-4" />
                {t('report:downloadDocx')}
              </Button>
            </>
          )}
          {isMobile && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size={isMobile ? 'icon' : 'default'}
                  className={cn(
                    isMobile && 'h-12 w-12 flex-none shrink-0 border-black/30',
                  )}
                  aria-label={t('report:reportActions')}>
                  <EllipsisVertical className="size-5" />
                  {!isMobile && t('report:reportActions')}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                side="top"
                align="end"
                className="w-max max-w-[min(20rem,90vw)]">
                <DropdownMenuItem
                  className="px-3 py-2.5 whitespace-normal"
                  onClick={() => {
                    const url = previewUrl ?? data?.pdfUrl;
                    if (Capacitor.getPlatform() === 'ios') {
                      if (url) NativePdfViewer.openPdf({ url });
                    } else {
                      setDrawerMode('preview');
                    }
                  }}>
                  <Eye className="size-4 shrink-0" />
                  {t('report:previewReport')}
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="px-3 py-2.5 whitespace-normal"
                  onClick={() => {
                    setDrawerMode('transcription');
                  }}>
                  <ScrollText className="size-4 shrink-0" />
                  {t('report:viewTranscription')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </ResponsiveContainer>

      {/* Mobile bottom sheets: preview (Android/Web) or transcription (both) */}
      <Drawer
        open={isDrawerOpen}
        onOpenChange={(open) => {
          if (!open) setDrawerMode(null);
        }}
        direction="bottom">
        <DrawerContent
          className="h-full max-h-[90vh]! w-full px-4"
          title={
            drawerMode === 'preview'
              ? t('report:pdfPreview')
              : t('home:transcription')
          }>
          <div
            className="flex flex-1 flex-col overflow-hidden"
            data-vaul-no-drag>
            {drawerMode === 'preview' && (previewUrl ?? data?.pdfUrl) && (
              <PdfViewer url={previewUrl ?? data?.pdfUrl ?? ''} />
            )}
            {drawerMode === 'transcription' && (
              <TranscriptionDisplay
                reportId={report._id}
                finalText={report.rawTranscription || ''}
                nonFinalText=""
                isTranscribing={false}
                isLoading={false}
                creationTime={report._creationTime}
                hideAudioControls={false}
              />
            )}
          </div>
        </DrawerContent>
      </Drawer>
    </div>
  );
};
